import { ReactNode, useEffect, useMemo } from 'react';

import { useBaseAuth } from '@cloudquery/base-users';
import { useLocation, useSearchParams, useNavigate } from 'react-router';

import { NotFound, SplashScreen, paths } from 'shared/base';

import { useAuth } from '@/context/auth';

import { authGuardIsAllowed } from './utils';

interface Props {
  children: ReactNode;
}

export function AuthGuard({ children }: Props) {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const location = useLocation();
  const team = /^\/teams\/([^/]+)/.exec(location.pathname)?.[1];
  const [searchParams] = useSearchParams();
  const returnTo = searchParams.get('returnTo') || undefined;
  const { isAuthenticated, isInitialized, manuallySignedOutRef, memberships } = useBaseAuth();
  const { registered } = useAuth();

  const authGuardIsAllowedResult = useMemo(
    () =>
      authGuardIsAllowed({
        isAuthenticated,
        isInitialized,
        manuallySignedOut: !!manuallySignedOutRef.current,
        pathname,
        registered,
        returnTo,
        searchParams,
      }),
    [
      isAuthenticated,
      isInitialized,
      manuallySignedOutRef,
      returnTo,
      pathname,
      registered,
      searchParams,
    ],
  );

  useEffect(() => {
    if (typeof authGuardIsAllowedResult === 'string') {
      navigate(authGuardIsAllowedResult);
    }
  }, [authGuardIsAllowedResult, navigate]);

  if (authGuardIsAllowedResult === false || typeof authGuardIsAllowedResult === 'string') {
    return <SplashScreen />;
  }

  if (team && !memberships.some(({ team: { name } }) => name === team)) {
    return <NotFound returnTo={paths.platform.index} />;
  }

  // If got here, it means that the redirect did not occur, and that tells us that the user is
  // authenticated / authorized.

  return <>{children}</>;
}
